/**
 * **우리동네TV 쿠폰(R-C) 관리 화면의 어휘·문구·판정 한 곳** — 순수 모듈이다(Vue·Element·`@/` 별칭 금지, 상대 import 는
 * `.js` 확장자까지 — node 내장 러너가 그대로 읽는다).
 *
 * 서버(appserver R-C)가 판정을 소유한다: 발행 가능 여부(`reasons`)·게시 상태(`status`, 저장하지 않고 파생)·KT 전송
 * 상태(`kt.state`)는 **어휘로만** 내려오고, 이 파일이 그 어휘를 문구·톤·버튼으로 옮긴다. 화면(`TvCouponPanel`)은
 * 조회·요청·그리기만 한다 — 상태 → 한글 매핑을 화면에 적지 않는다(`partnerLink.js` 와 같은 결).
 *
 * 권한은 **서버 액션**이 답한다([TV_COUPON_ACTION]) — 역할 이름을 적지 않는다. 재전송이 `admin`·`mng` 뿐인 것도
 * 서버 SQL 032 가 그 액션을 두 역할에만 준 결과다.
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/R-C-우리동네TV쿠폰-appserver-report.md` §4 「★ admin-web 계약」·
 * §10(할인 표시 = **현재 할인**, 발행 스냅샷 없음).
 */
import { offerStatusBadge } from './discountOffer.js'
import { formatterNum } from './formatterNum.js'
import { PARTNER_LINK_TEXT } from './partnerLink.js'

/** 관리 API base — `/api` 밖(세션 인증, OAuth 체인 무관). 경로 리터럴을 화면에 적지 않는다 */
export const TV_COUPON_API = '/tv-coupons'

/** 세션 액션 id — appserver `sql/db_changes_2026-09-23_tv-coupon-auth.sql`(plan 032)의 `sys_action` 과 같은 문자열 */
export const TV_COUPON_ACTION = Object.freeze({
  /** `GET /tv-coupons/eligibility` — 4롤 */
  ELIGIBILITY: 'TvCouponEligibility',
  /** `GET /tv-coupons` — 4롤. 탭을 그리는 기준이다(없으면 탭이 없다 — 누르면 403 = 로그아웃일 요청을 보내지 않는다) */
  LIST: 'TvCouponList',
  /** `POST /tv-coupons` — 4롤 */
  PUBLISH: 'TvCouponPublish',
  /** `PUT /tv-coupons/{cd}` — 4롤 */
  UPDATE: 'TvCouponUpdate',
  /** `PUT /tv-coupons/{cd}/stop` — 4롤 */
  STOP: 'TvCouponStop',
  /** `POST /tv-coupons/{cd}/resend` — admin·mng 만 */
  RESEND: 'TvCouponResend',
})

/** 제목 상한 — 서버 `TvCoupon.TITLE_MAX` 와 같은 값(넘으면 서버가 400) */
export const TV_COUPON_TITLE_MAX = 50

/** 게시 상태 어휘 — 서버 `TvCouponStatus` */
export const TV_COUPON_STATUS = Object.freeze({
  OPEN: 'OPEN',
  NOT_STARTED: 'NOT_STARTED',
  ENDED: 'ENDED',
  STOPPED: 'STOPPED',
  EXHAUSTED: 'EXHAUSTED',
})

/** KT 전송 상태 어휘 — 서버 아웃박스 `status`(마지막 op 의 것) */
export const TV_COUPON_KT_STATE = Object.freeze({
  PENDING: 'PENDING',
  SENT: 'SENT',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
})

/** 발행을 막는 사유 어휘 — 서버 `TvCouponIneligibility`(순서가 곧 표시 순서다) */
export const TV_COUPON_INELIGIBILITY = Object.freeze({
  NOT_SUBSCRIBED: 'NOT_SUBSCRIBED',
  INVALID_BIZ_NO: 'INVALID_BIZ_NO',
  WRITE_DISABLED: 'WRITE_DISABLED',
  NO_COUPON_DISCOUNT: 'NO_COUPON_DISCOUNT',
})

/** 화면 문구 — 화면에 다시 타이핑하지 않는다 */
export const TV_COUPON_TEXT = Object.freeze({
  tabTitle: '우리동네TV 쿠폰',
  panelTitle: '우리동네TV 쿠폰',

  publishAction: '쿠폰 발행',
  refreshAction: '새로고침',
  editAction: '수정',
  stopAction: '중지',
  resendAction: '재전송',
  copyAction: '복사',
  retryAction: '다시 시도',
  saveAction: '저장',
  cancelAction: '취소',
  closeAction: '닫기',

  titleColumn: '제목 · 할인',
  statusColumn: '상태',
  periodColumn: '게시 기간',
  claimColumn: '받은 사람',
  ktColumn: 'KT 전송',
  landingColumn: '랜딩 주소',
  manageColumn: '관리',

  noStore: '매장을 먼저 저장한 뒤 우리동네TV 쿠폰을 발행할 수 있습니다.',
  empty: '발행한 우리동네TV 쿠폰이 없습니다.',
  loadFailed: '우리동네TV 쿠폰을 불러오지 못했습니다.',
  blockedTitle: '지금은 이 매장에서 우리동네TV 쿠폰을 발행할 수 없습니다.',
  copied: '복사했습니다.',
  saved: '저장했습니다. KT 전송은 잠시 뒤 반영됩니다.',
  stopped: '중지했습니다. KT 전송은 잠시 뒤 반영됩니다.',
  resent: '다시 보내도록 했습니다. 잠시 뒤 새로고침하세요.',
  copyFailed: '복사하지 못했습니다. 값을 직접 선택해 복사하세요.',
  /** 삭제된 할인 — 서버가 현재 할인을 못 찾으면 이름·종류·값이 null 이다(보고서 §10) */
  discountMissing: '(할인이 없어짐)',

  publishDialogTitle: '우리동네TV 쿠폰 발행',
  editDialogTitle: '우리동네TV 쿠폰 수정',
  resultDialogTitle: '우리동네TV 쿠폰 발행됨',
  discountLabel: '할인',
  discountPlaceholder: '게시할 할인을 고르세요',
  /**
   * 발행 창의 할인 칸 아래 한 줄 — 고를 수 있는 할인은 서버가 거른 목록(`eligibility.discounts`)이고, KT R-T 결정 ①
   * (2026-10-12)부터 쿠폰 전용 할인만이다. 종전에는 쿠폰 사용 가능 할인을 모두 골랐으므로 바뀐 사실을 이 창에서만 말한다.
   */
  discountRule: '쿠폰은 쿠폰 전용 할인으로만 발행할 수 있습니다.',
  titleLabel: '제목',
  /** 발행 — 제목을 비우면 서버가 할인 이름을 쓴다 */
  titlePublishHint: '비우면 할인 이름으로 게시합니다.',
  /** 수정 — 빈 칸은 서버가 「그대로」로 읽는다(`PUT` 부분 수정) */
  titleEditHint: '비우면 지금 제목을 그대로 둡니다.',
  periodLabel: '게시 기간',
  periodStart: '게시 시작일',
  periodEnd: '게시 끝 날짜',
  landingLabel: '랜딩 주소',
  landingPending: '발행하면 정해집니다. TV 화면의 QR 로 만들어지므로 발행 뒤에는 바뀌지 않습니다.',
  resultHead: '발행했습니다. KT 전송은 잠시 뒤 목록에 반영됩니다.',

  stopConfirmTitle: '우리동네TV 쿠폰 중지',
  stopConfirmButton: '중지',
  stopConfirmTail:
    '중지하면 랜딩에서 더 이상 쿠폰을 받을 수 없고 KT 에 게시 내림을 보냅니다. 이미 받은 쿠폰은 유효기간까지 쓸 수 있습니다. 중지한 쿠폰은 다시 열 수 없으니, 다시 게시하려면 새로 발행하세요.',

  needDiscount: '게시할 할인을 고르세요.',
  needPeriod: '게시 기간을 고르세요.',
  /** 서버 `TvCouponDrafts` 문구와 같은 문장 — 서버에 닿기 전에 같은 말로 막는다 */
  titleTooLong: '제목은 50자까지 입력할 수 있습니다.',

  /** 화면 아래 안내 — 계약의 사실(보고서 §1-1·§10) */
  notes: Object.freeze([
    '게시 기간은 고객이 쿠폰을 받을 수 있는 기간입니다. 받은 쿠폰은 할인의 쿠폰 유효기간 동안 쓸 수 있습니다.',
    '랜딩 주소는 TV 화면의 QR 로 만들어지므로 바뀌지 않습니다. 발행 뒤에는 제목과 게시 기간만 고칠 수 있습니다.',
    '할인 표시는 지금 할인 설정을 따릅니다. 게시 중에 할인을 고치면 랜딩과 새로 받는 쿠폰도 바뀌고, KT 에 보낸 제목은 수정해야 바뀝니다.',
    'KT 전송은 잠시 뒤 반영됩니다(새로고침). 전송이 끝나도 TV 노출은 KT 내부 절차를 거칩니다.',
  ]),
})

/** 발행 막힘 사유 → 문장. 같은 사실은 같은 문장이다 — 쓰기 꺼짐은 외부 채널 연동 패널과 같은 문구를 쓴다 */
const INELIGIBILITY_TEXT = Object.freeze({
  [TV_COUPON_INELIGIBILITY.NOT_SUBSCRIBED]:
    '우리동네TV 에 가입(입점)한 매장만 발행할 수 있습니다. 외부 채널 연동 탭에서 가입 상태를 확인하세요.',
  [TV_COUPON_INELIGIBILITY.INVALID_BIZ_NO]:
    '사업자번호가 KT 규격에 맞지 않습니다. 사업자정보 탭에서 사업자번호를 고친 뒤 새로고침하세요.',
  [TV_COUPON_INELIGIBILITY.WRITE_DISABLED]: PARTNER_LINK_TEXT.blockedWriteDisabled,
  [TV_COUPON_INELIGIBILITY.NO_COUPON_DISCOUNT]:
    "게시할 수 있는 할인이 없습니다. 할인 관리에서 '쿠폰 전용'인 주문 할인(정액·정률)을 먼저 만드세요. 메뉴 할인은 게시할 수 없습니다.",
})

/**
 * 발행 막힘 사유 한 줄. **모르는 사유도 말은 한다**(서버가 어휘를 늘렸을 때 버튼만 조용히 꺼지지 않게) — 코드를 붙인다.
 * @param {string} reason 서버 `reasons[]` 한 건
 */
export function tvCouponIneligibilityText(reason) {
  return INELIGIBILITY_TEXT[reason] || `지금은 발행할 수 없습니다. (${reason})`
}

/**
 * **발행 자격 한 곳** — eligibility 응답(또는 못 읽음)을 화면이 쓸 모양으로.
 *
 * - 응답이 없으면(조회 전·실패·액션 없음) 발행할 수 없고 사유 줄도 없다 — 모르는 것을 지어내지 않는다.
 * - `publishable` 은 서버 값이 곧 답이다. 할인 목록이 비어 있으면 서버가 `NO_COUPON_DISCOUNT` 로 이미 막는다.
 *
 * @param {?{publishable: boolean, reasons: ?Array<string>, discounts: ?Array<object>}} eligibility
 * @returns {{publishable: boolean, reasonLines: Array<string>, discounts: Array<object>}}
 */
export function tvCouponEligibilityView(eligibility) {
  if (!eligibility) return Object.freeze({ publishable: false, reasonLines: [], discounts: [] })

  const reasons = Array.isArray(eligibility.reasons) ? eligibility.reasons : []

  return Object.freeze({
    publishable: eligibility.publishable === true && reasons.length === 0,
    reasonLines: reasons.map(tvCouponIneligibilityText),
    discounts: Array.isArray(eligibility.discounts) ? eligibility.discounts : [],
  })
}

/**
 * 할인 값 한 줄 — `3,000원 할인` · `10% 할인`. 종류는 공개 표기 대문자(`PRICE`·`PERCENT`)다; 그 밖(할인이 없어짐 등)은 `-`.
 * 이 화면의 할인 값은 **현재 할인**이다(보고서 §10 — 발행 스냅샷 없음).
 */
export function tvCouponDiscountValueText(type, value) {
  if (value == null || value === '') return '-'
  if (type === 'PRICE') return `${formatterNum(null, null, value)}원 할인`
  if (type === 'PERCENT') return `${value}% 할인`

  return '-'
}

/** 할인 상태 배지 문구 — R-B 의 어휘·문구(`discountOffer.offerStatusBadge`)를 그대로 쓴다. 모르는 값은 코드 */
const offerStatusLabel = (offerStatus) => {
  const badge = offerStatusBadge(offerStatus)

  return badge ? badge.label : offerStatus || ''
}

/**
 * 발행 다이얼로그의 할인 선택지 한 줄 — `가을맞이 할인 · 3,000원 할인 · 받은 날부터 30일`. 지금 발급이 멈춘 할인은
 * 그 이유(R-B 배지 문구)를 뒤에 붙인다 — 서버는 그런 할인도 고르게 두지만, 고객은 받을 수 없다([tvCouponDiscountWarning]).
 */
export function tvCouponDiscountLabel(discount) {
  if (!discount) return ''

  const parts = [
    discount.discountNm || TV_COUPON_TEXT.discountMissing,
    tvCouponDiscountValueText(discount.type, discount.value),
  ]
  if (discount.validityDays != null) parts.push(`받은 날부터 ${discount.validityDays}일`)

  const label = parts.join(' · ')

  return discount.offerStatus && discount.offerStatus !== 'OPEN'
    ? `${label} [${offerStatusLabel(discount.offerStatus)}]`
    : label
}

/**
 * 고른 할인이 **지금 쿠폰을 내줄 수 없으면** 그 경고 — 서버 판정: 할인의 R-B 상태가 `OPEN` 이 아니면 TV 쿠폰은 게시 중이어도
 * `EXHAUSTED`(받을 수 없음)다(보고서 §10-1 `TvCouponOffer.open`). 발행은 막지 않는다(할인 게시 기간이 곧 열릴 수 있다).
 * @returns {?string} 경고가 없으면 `null`
 */
export function tvCouponDiscountWarning(discount) {
  if (!discount || !discount.offerStatus || discount.offerStatus === 'OPEN') return null

  return `이 할인은 지금 쿠폰을 발급하지 않습니다(${offerStatusLabel(
    discount.offerStatus
  )}). 게시해도 발급이 다시 열릴 때까지 고객이 받을 수 없습니다.`
}

/**
 * 게시 상태 배지 — 서버 `status` → 문구·톤·도움말. 모르는 값은 코드 그대로(빈칸을 만들지 않는다).
 * `EXHAUSTED` 는 게시가 살아 있지만 바탕 할인이 지금 쿠폰을 내줄 수 없는 상태다(소진·기간 밖·사용 중지·메뉴 대상으로 바뀜·삭제).
 */
const STATUS_BADGE = Object.freeze({
  [TV_COUPON_STATUS.OPEN]: Object.freeze({ label: '게시 중', type: 'success', note: null }),
  [TV_COUPON_STATUS.NOT_STARTED]: Object.freeze({ label: '게시 전', type: '', note: null }),
  [TV_COUPON_STATUS.ENDED]: Object.freeze({ label: '게시 끝', type: 'info', note: null }),
  [TV_COUPON_STATUS.STOPPED]: Object.freeze({ label: '중지됨', type: 'info', note: null }),
  [TV_COUPON_STATUS.EXHAUSTED]: Object.freeze({
    label: '발급 멈춤',
    type: 'warning',
    note: '바탕 할인이 소진됐거나 할인 기간 밖·사용 중지 상태라 지금은 받을 수 없습니다.',
  }),
})

/** @returns {{label: string, type: string, note: ?string}} */
export function tvCouponStatusBadge(status) {
  return STATUS_BADGE[status] || Object.freeze({ label: status || '-', type: 'info', note: null })
}

/** KT op 어휘 → 낱말(C 등록 · U 수정 · D 중지 — 화면의 버튼 낱말과 같다) */
const KT_OP_TEXT = Object.freeze({ C: '등록', U: '수정', D: '중지' })

const KT_STATE_CHIP = Object.freeze({
  [TV_COUPON_KT_STATE.PENDING]: Object.freeze({
    text: '전송 대기',
    tagType: '',
    note: 'KT 전송을 기다리는 중입니다. 잠시 뒤 새로고침하세요.',
  }),
  [TV_COUPON_KT_STATE.SENT]: Object.freeze({
    text: '전송 완료',
    tagType: 'success',
    note: '전송이 끝나도 TV 노출은 KT 내부 절차를 거칩니다.',
  }),
  [TV_COUPON_KT_STATE.FAILED]: Object.freeze({ text: '전송 실패', tagType: 'danger', note: null }),
  [TV_COUPON_KT_STATE.SKIPPED]: Object.freeze({ text: '보내지 않음', tagType: 'info', note: null }),
})

/**
 * **KT 전송 칩 한 곳** — 목록 행의 `kt`(마지막 op 의 전송 상태) → 칩. 전송 행이 없으면(`kt: null`) `null` 이고 화면은 `-` 다.
 *
 * 칩 문구는 「op · 상태」(`수정 · 전송 실패`)다 — 같은 실패라도 무엇을 보내다 실패했는지가 다음 할 일을 정한다.
 * 도움말 줄에는 마지막 사유(`lastError` — 재시도 대기 중인 PENDING 도 싣는다)·접수 번호·마지막 시도 시각을 순서대로 싣는다.
 *
 * @param {?{state: string, lastOp: ?string, receiptId: ?string, lastError: ?string, lastAttemptAt: ?string}} kt
 * @returns {?{text: string, tagType: string, lines: Array<string>}}
 */
export function tvCouponKtChip(kt) {
  if (!kt) return null

  const base = KT_STATE_CHIP[kt.state] || Object.freeze({ text: kt.state || '-', tagType: 'info', note: null })
  const op = KT_OP_TEXT[kt.lastOp] || kt.lastOp || ''
  const lines = [
    base.note,
    kt.lastError ? `사유: ${kt.lastError}` : null,
    kt.receiptId ? `KT 접수 번호: ${kt.receiptId}` : null,
    kt.lastAttemptAt ? `마지막 시도: ${kt.lastAttemptAt}` : null,
  ].filter(Boolean)

  return Object.freeze({ text: op ? `${op} · ${base.text}` : base.text, tagType: base.tagType, lines })
}

/** 게시가 내려간 상태 — 서버 `TvCouponStatus.takenDown` 과 같은 두 값. 수정(409)·중지를 닫는 기준이다 */
const TAKEN_DOWN = Object.freeze([TV_COUPON_STATUS.STOPPED, TV_COUPON_STATUS.ENDED])

/** 재전송이 여는 전송 상태 — 서버는 FAILED 와 SKIPPED 를 함께 다시 연다(보고서 §5-4) */
const RESENDABLE = Object.freeze([TV_COUPON_KT_STATE.FAILED, TV_COUPON_KT_STATE.SKIPPED])

/**
 * **행 버튼 한 곳** — 상태와 세션 액션으로 정한다(역할 이름 없음).
 *
 * - 수정·중지: 게시가 살아 있을 때(`STOPPED`·`ENDED` 아님). 서버도 그 둘의 수정을 409 로 막는다.
 * - 재전송: 마지막 전송이 `FAILED`·`SKIPPED` 이고 재전송 액션(admin·mng)이 있을 때.
 *
 * @param {object} row 목록 행
 * @param {{update: boolean, stop: boolean, resend: boolean}} can 세션 액션 보유
 */
export function tvCouponRowActions(row, can) {
  const allowed = can || {}
  const live = !!row && TAKEN_DOWN.indexOf(row.status) < 0
  const ktState = row && row.kt ? row.kt.state : null

  return Object.freeze({
    edit: live && allowed.update === true,
    stop: live && allowed.stop === true,
    resend: allowed.resend === true && RESENDABLE.indexOf(ktState) > -1,
  })
}

/** 게시 기간 한 줄 — `2026-10-08 ~ 2026-10-31` */
export function tvCouponPeriodText(row) {
  if (!row || !row.postStartDate || !row.postEndDate) return '-'

  return `${row.postStartDate} ~ ${row.postEndDate}`
}

/** 목록의 할인 줄 — 현재 할인의 이름과 값(할인이 없어지면 그 사실) */
export function tvCouponRowDiscountText(row) {
  if (!row) return ''

  return `${row.discountNm || TV_COUPON_TEXT.discountMissing} · ${tvCouponDiscountValueText(
    row.discountType,
    row.discountValue
  )}`
}

/** 받은 사람 수 — 천 단위 구분 */
export function tvCouponClaimText(row) {
  return String(formatterNum(null, null, row && row.claimCount != null ? row.claimCount : 0))
}

/** 중지 확인창 본문 — 무엇이 멈추는지 먼저 말한다 */
export function tvCouponStopConfirmMessage(row) {
  return `「${(row && row.title) || ''}」 게시를 중지합니다. ${TV_COUPON_TEXT.stopConfirmTail}`
}

/** 한 쿠폰의 경로 — 수정 `PUT`, 중지 `PUT …/stop`, 재전송 `POST …/resend` */
export function tvCouponUrl(tvCouponCd, action) {
  const base = `${TV_COUPON_API}/${tvCouponCd}`

  return action ? `${base}/${action}` : base
}

/**
 * 다이얼로그 초안 — 발행은 빈 초안, 수정은 그 행의 지금 값. 기간은 날짜 범위 선택기의 모양(`[시작, 끝]`, `YYYY-MM-DD`)이다.
 * @param {?object} row 수정할 행(없으면 발행)
 */
export function tvCouponDraft(row) {
  if (!row) return { discountCd: null, title: '', period: null }

  return {
    discountCd: row.discountCd,
    title: row.title || '',
    period: row.postStartDate && row.postEndDate ? [row.postStartDate, row.postEndDate] : null,
  }
}

const trimmedTitle = (title) => (typeof title === 'string' ? title.trim() : '')

const periodOf = (draft) =>
  draft && Array.isArray(draft.period) && draft.period.length === 2 && draft.period[0] && draft.period[1]
    ? draft.period
    : null

/**
 * 보내기 전에 막을 것 — 비어 있어 **보낼 수 없는** 칸과 서버와 같은 제목 상한만 본다. 날짜 규칙(끝 ≥ 오늘 등)은 서버가
 * 판정하고 그 문장을 그대로 보인다(서버 「오늘」은 KST 다 — 보는 사람의 시계로 다시 판정하지 않는다).
 *
 * @param {object} draft [tvCouponDraft] 모양
 * @param {boolean} publishing 발행이면 할인이 필수다(수정은 할인을 바꾸지 않는다)
 * @returns {?string} 막을 이유(문구) — 없으면 `null`
 */
export function tvCouponDraftProblem(draft, publishing) {
  if (publishing && (!draft || draft.discountCd == null || draft.discountCd === '')) return TV_COUPON_TEXT.needDiscount
  if (!periodOf(draft)) return TV_COUPON_TEXT.needPeriod
  if (trimmedTitle(draft.title).length > TV_COUPON_TITLE_MAX) return TV_COUPON_TEXT.titleTooLong

  return null
}

/**
 * 발행 본문 — `POST /tv-coupons`. 빈 제목은 `null`(서버가 할인 이름을 쓴다).
 * @param {number} storeCd
 * @param {object} draft [tvCouponDraftProblem] 을 통과한 초안
 */
export function tvCouponPublishBody(storeCd, draft) {
  const period = periodOf(draft) || [null, null]

  return {
    storeCd,
    discountCd: draft.discountCd,
    title: trimmedTitle(draft.title) || null,
    postStartDate: period[0],
    postEndDate: period[1],
  }
}

/** 수정 본문 — `PUT /tv-coupons/{cd}`. 빈 제목은 `null`(서버가 「그대로」로 읽는다). 값이 같으면 서버가 전송 없이 돌려준다 */
export function tvCouponReviseBody(draft) {
  const period = periodOf(draft) || [null, null]

  return {
    title: trimmedTitle(draft.title) || null,
    postStartDate: period[0],
    postEndDate: period[1],
  }
}

/**
 * 쓰기 실패 뒤 할 일 — 문장은 인터셉터가 **서버 문장 그대로** 이미 띄웠다. 404(그 사이 없어짐·범위 밖)·409(그 사이 중지·게시
 * 끝, 다시 보낼 것 없음)는 화면이 낡았다는 뜻이라 다시 읽는다(`RELOAD`). 그 밖(400 등)은 고쳐서 다시 보내면 되므로 그대로 둔다.
 *
 * @param {?number} status `HttpError.status`(전송 오류는 `null`)
 * @returns {'RELOAD'|'KEEP'}
 */
export function tvCouponWriteFailure(status) {
  return status === 404 || status === 409 ? 'RELOAD' : 'KEEP'
}
